import React from 'react';
import styled from 'styled-components';
import Grid from '@material-ui/core/Grid';
import SGTitle from 'components/Typography/SGTitle';
import SGSubtitle from 'components/Typography/SGSubtitle';
import Accordion from 'features/POSRegisters/components/Accordion';
import getPriorityAlert from 'features/POSRegisters/functions/getPriorityAlert';
import ScanDetails from 'features/POSRegisters/interfaces/ScanDetails';
import Device from 'features/POSRegisters/interfaces/Device';
import DeviceIcon from 'features/POSRegisters/components/DeviceIcon';
import DeviceType from 'features/POSRegisters/interfaces/DeviceType';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import {getSeverityString} from 'features/POSRegisters/functions/alertTypes';
import {AlertType as AlertTypeEnum} from 'features/POSRegisters/interfaces/AlertInfo';
import type AlertType from 'features/POSRegisters/interfaces/AlertInfo';

//#region styles
const StyledGridAlert = styled(Grid)`
  display: flex;
  gap: 16px;

  .MuiAccordion-root {
    width: 100%;
  }
`;

const StyledAlertDetailsContainer = styled(Grid)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 8px 68px 0;
  gap: 16px;
`;

const StyledAlertTitle = styled(SGSubtitle)`
  margin-bottom: 16px;

  &.scanfailure {
    color: ${props => props.theme.informationalBlue};
  }

  &.minor {
    color: ${props => props.theme.yellow};
  }

  &.major {
    color: ${props => props.theme.orange};
  }

  &.critical {
    color: ${props => props.theme.red};
  }
`;

const StyledShortDescription = styled(StyledAlertTitle)`
  margin-bottom: 0px;
  text-transform: none;
  font-weight: 500;
`;

const StyledAlertDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.full-row {
    grid-column-start: 1;
    grid-column-end: 3;
  }
`;

const StyledAlertDetailsTitle = styled(SGTitle)`
  text-transform: uppercase;
  &.scanfailure {
    color: ${props => props.theme.disabledStateBlue};
  }

  &.minor {
    color: ${props => props.theme.yellow};
  }

  &.major {
    color: ${props => props.theme.orange};
  }

  &.critical {
    color: ${props => props.theme.red};
  }
`;

const StyledIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.white};

  div:first-child {
    height: 24px;
  }

  div:first-child > svg {
    font-size: 24px;
  }

  > svg {
    position: absolute;
    top: -2px;
    right: -4px;
    z-index: 10;
  }
`;

//#endregion styles
export default function Alert({
  alert,
  device,
  rows,
}: {
  alert: AlertType;
  device: Device<ScanDetails> & {type?: DeviceType};
  rows: {
    label?: string;
    value?: string | React.ReactNode;
    fullWidth?: boolean;
  }[];
}) {
  if (!device) return null;

  return (
    <StyledGridAlert container>
      <Accordion
        className={`${getPriorityAlert([device])}`}
        type={alert.alertType}
        title={getSeverityString(alert)}
        icon={
          device.type &&
          alert.alertType !== AlertTypeEnum.DIAGNOSTIC_ALERT && (
            <StyledIcon>
              <DeviceIcon deviceType={device.type as DeviceType} />
              <SeverityBadge severity={alert.siteImpact} size={0.625} />
            </StyledIcon>
          )
        }
        header={
          <StyledShortDescription className={alert.siteImpact.toLowerCase()} variant="s2">
            {alert.shortDescription}
          </StyledShortDescription>
        }
        severity={alert.siteImpact}
      >
        <StyledAlertDetailsContainer>
          {rows.map((row, index) => {
            if (React.isValidElement(row?.value)) {
              return (
                <div
                  style={row?.fullWidth ? {gridColumnStart: 1, gridColumnEnd: 3} : {}}
                  key={index}
                >
                  {row?.value}
                </div>
              );
            } else if (typeof row === 'object' && row !== null && 'label' in row) {
              return (
                <StyledAlertDetails key={row?.label} className={row?.fullWidth ? 'full-row' : ''}>
                  <StyledAlertDetailsTitle className={alert.siteImpact.toLowerCase()} variant="t6">
                    {row?.label}
                  </StyledAlertDetailsTitle>
                  <SGSubtitle className="s2">{row?.value}</SGSubtitle>
                </StyledAlertDetails>
              );
            }
          })}
        </StyledAlertDetailsContainer>
      </Accordion>
    </StyledGridAlert>
  );
}
